// The viewer shell: reads the code from the URL, fetches the manifest and shows the first
// screen. three.js, the runtime, MindAR and WebXR load behind it, only as far as the visitor needs.
// No cookies, no storage, no third-party scripts: children use these experiences.
import "./styles.css";
import type { ArProblem } from "./ar.ts";
import type { Experience } from "./experience.ts";
import { type MessageKey, pickLocale, pickUiLanguage, translate, type UiLanguage } from "./i18n.ts";
import { type IconName, icon } from "./icons.ts";
import {
  codeFromPath,
  collectionFromPath,
  draftPreviewUrl,
  fetchCollection,
  fetchManifest,
  publicationUrl,
  reportUrl,
} from "./publication.ts";
import type { XrStatus } from "./xr.ts";

/** The little the shell reads from the manifest before it is fully validated; text only. */
type ManifestPreview = {
  code: string;
  title: Record<string, string>;
  defaultLocale: string;
  locales: string[];
  audience: { children: boolean };
  anchor: { type: string; tracking?: string; placement?: string; image?: string };
  cover?: string;
};

/** How the experience is used: on the card, card then walk around, on a floor or wall, or 3D only. */
type Mode = "card" | "world" | "floor" | "wall" | "3d";

const root = document.getElementById("app") as HTMLElement;
const params = new URLSearchParams(location.search);
const asked = params.get("lang");
/** The visitor's languages, an explicit ?lang= first. */
const preferred = [...(asked ? [asked] : []), ...navigator.languages];
const lang: UiLanguage = pickUiLanguage(preferred);
const t = (key: MessageKey, vars?: Record<string, string>) => translate(lang, key, vars);

type Child = Node | string | null | undefined | false;

/** A tiny element builder: text is always set as text, never parsed as HTML. */
function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<Record<string, string | boolean | ((event: Event) => void)>> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [name, value] of Object.entries(props)) {
    if (typeof value === "function") el.addEventListener(name.slice(2).toLowerCase(), value);
    else if (value === true) el.setAttribute(name, "");
    else if (typeof value === "string") el.setAttribute(name, value);
  }
  for (const child of children) if (child) el.append(child);
  return el;
}

function show(...children: Child[]) {
  root.replaceChildren(...children.filter((c): c is Node | string => Boolean(c)));
}

const button = (label: string, onclick: () => void, className = "btn", glyph?: IconName) =>
  h("button", { type: "button", class: className, onclick }, glyph && icon(glyph), h("span", {}, label));

/** "Made with ARCraft", and on an experience's own screens a small link to report it. */
function brand(withReport = false) {
  return h(
    "p",
    { class: "made-with" },
    h("span", { class: "brand-mark", "aria-hidden": "true" }),
    t("madeWith"),
    withReport &&
      manifest &&
      h(
        "a",
        {
          href: reportUrl(manifest.code, location, lang),
          class: "report-link",
          target: "_blank",
          rel: "noopener",
        },
        t("report"),
      ),
  );
}

/** Switches the interface language with ?lang=, keeping everything else. */
function languageSwitch() {
  const other = lang === "ar" ? "en" : "ar";
  const url = new URL(location.href);
  url.searchParams.set("lang", other);
  return h(
    "a",
    { class: "chip", href: url.pathname + url.search, lang: other, hreflang: other },
    icon("globe", 16),
    t("language"),
  );
}

function toast(text: string) {
  const el = h("div", { class: "toast", role: "status" }, text);
  document.body.append(el);
  setTimeout(() => el.remove(), 2200);
}

async function share(title: string) {
  const url = `${location.origin}${location.pathname}`;
  try {
    if (navigator.share) await navigator.share({ title, url });
    else {
      await navigator.clipboard.writeText(url);
      toast(t("copied"));
    }
  } catch {
    // Dismissed share sheets reject; nothing to do.
  }
}

function setLanguage() {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
}

// ---------- boot ----------

let manifest: ManifestPreview;
/** The content language: one the scene has, chosen from the visitor's languages. */
let locale = "en";
let mode: Mode = "3d";
let experience: Promise<Experience> | undefined;
let running: { stop(): void } | null = null;
let roomSupport: Promise<boolean> = Promise.resolve(false);

function loading() {
  show(
    h(
      "div",
      { class: "screen center", role: "status" },
      h("span", { class: "loader", "aria-hidden": "true" }),
      h("p", { class: "muted" }, t("loading")),
    ),
  );
}

async function boot() {
  setLanguage();
  const collection = collectionFromPath(location.pathname);
  if (collection) return collectionPage(collection);
  const code = codeFromPath(location.pathname);
  if (!code) return notFound(null);
  loading();

  const result = await fetchManifest(code);
  if (result.kind === "missing") return notFound(code);
  if (result.kind === "error") return loadFailed();
  manifest = result.manifest as ManifestPreview;
  mode = modeOf(manifest.anchor);

  locale = pickLocale(manifest.locales ?? [], manifest.defaultLocale, preferred);
  document.title = titleText();

  // Start the heavy part straight away; the visitor reads the first screen meanwhile.
  experience = import("./experience.ts").then((m) => m.loadExperience(code, result.manifest));
  experience.catch(() => {});
  if (mode === "world" || mode === "floor" || mode === "wall") {
    roomSupport = import("./xr.ts").then((m) => m.xrSupported()).catch(() => false);
  }

  const desktop = matchMedia("(hover: hover) and (pointer: fine)").matches;
  if (desktop) return desktopView();
  intro();
}

function modeOf(anchor: ManifestPreview["anchor"]): Mode {
  if (anchor?.type === "image") return anchor.tracking === "world" ? "world" : "card";
  if (anchor?.type === "surface") return anchor.placement === "vertical" ? "wall" : "floor";
  return "3d";
}

const MODE_ICON: Record<Mode, IconName> = {
  card: "card",
  world: "walk",
  floor: "floor",
  wall: "wall",
  "3d": "cube",
};

function titleText() {
  return manifest.title?.[locale] ?? Object.values(manifest.title ?? {})[0] ?? document.title;
}

/** The scene's title in its own language, which can differ from the interface's. */
function title(level: "h1" | "h2" = "h1") {
  return h(level, { lang: locale, dir: "auto", class: "title" }, titleText());
}

/** The picture behind the intro: the published cover, else the printed card. */
function coverUrl(m: ManifestPreview) {
  if (m.cover) return publicationUrl(m.code, m.cover);
  if (m.anchor?.type === "image" && m.anchor.image) return publicationUrl(m.code, m.anchor.image);
  return null;
}

function badge(m: Mode) {
  return h("span", { class: `badge badge-${m}` }, icon(MODE_ICON[m], 14), t(`badge.${m}`));
}

// ---------- screens ----------

function notFound(code: string | null) {
  show(
    h(
      "main",
      { class: "screen center" },
      h("div", { class: "empty-art", "aria-hidden": "true" }, icon("scan", 40)),
      h("h1", {}, t("notFoundTitle")),
      h("p", { class: "muted" }, t("notFoundBody")),
      code && h("a", { href: draftPreviewUrl(code, location), class: "quiet-link" }, t("teamDraft")),
      brand(),
    ),
  );
}

function loadFailed() {
  show(
    h(
      "main",
      { class: "screen center" },
      h("div", { class: "empty-art", "aria-hidden": "true" }, icon("refresh", 40)),
      h("p", { role: "alert" }, t("loadFailed")),
      button(t("retry"), () => location.reload(), "btn", "refresh"),
    ),
  );
}

function updateNeeded() {
  show(
    h(
      "main",
      { class: "screen center" },
      h("p", { role: "alert" }, t("update")),
      button(t("retry"), () => location.reload(), "btn", "refresh"),
    ),
  );
}

/** Waits for the experience; shows the right message if it can't be used. */
async function ready(): Promise<Experience | null> {
  try {
    return await (experience as Promise<Experience>);
  } catch (error) {
    const { UpdateNeeded } = await import("./experience.ts");
    if (error instanceof UpdateNeeded) updateNeeded();
    else loadFailed();
    return null;
  }
}

/** The phone's first screen: a cover, what to do, and the buttons to do it. */
function intro(problem?: ArProblem | "noRoom") {
  const cover = coverUrl(manifest);
  const steps =
    mode === "3d" ? [] : ([1, 2, 3] as const).map((n) => t(`steps.${mode as Exclude<Mode, "3d">}.${n}`));
  const primary =
    mode === "3d" ? null : button(t("start"), () => void start(), "btn btn-lg btn-primary", "camera");
  show(
    h(
      "main",
      { class: "intro" },
      h(
        "div",
        { class: `hero${cover ? "" : " hero-art"}` },
        cover && h("img", { src: cover, alt: "", class: "hero-img", decoding: "async" }),
        h(
          "div",
          { class: "hero-top" },
          badge(mode),
          h(
            "div",
            { class: "hero-actions" },
            languageSwitch(),
            h(
              "button",
              {
                type: "button",
                class: "chip",
                onclick: () => void share(titleText()),
                "aria-label": t("share"),
              },
              icon("share", 16),
            ),
          ),
        ),
      ),
      h(
        "section",
        { class: "sheet" },
        title(),
        h("p", { class: "lead" }, t(mode === "3d" ? "intro.3d" : `intro.${mode}`)),
        steps.length > 0 && h("ol", { class: "steps" }, ...steps.map((s) => h("li", {}, s))),
        problem &&
          h(
            "p",
            { class: "error", role: "alert" },
            problem === "noRoom" ? t("xr.noRoom") : t(`errors.${problem}`),
          ),
        h(
          "div",
          { class: "actions" },
          primary,
          button(
            t("view3d"),
            () => void open3d(),
            primary ? "btn btn-quiet" : "btn btn-lg btn-primary",
            "cube",
          ),
        ),
        mode !== "3d" && h("p", { class: "small muted privacy" }, t("privacy")),
        brand(true),
      ),
    ),
  );
}

/** "Start": the room modes use WebXR where the phone has it; otherwise the card or 3D. */
async function start() {
  if (mode === "card") return startCamera();
  const room = await roomSupport;
  if (room) return startRoom(mode === "world" ? "world" : "surface");
  if (mode === "world") return startCamera(); // keeps the scene on the card instead
  return open3d("noRoom");
}

// ---------- the card (MindAR) ----------

async function startCamera() {
  const stage = h("div", { class: "stage" });
  const status = h("span", { class: "pill" }, icon("scan", 16), h("span", {}, t("starting")));
  const frame = h(
    "div",
    { class: "scan-frame", "aria-hidden": "true" },
    h("i"),
    h("i"),
    h("i"),
    h("i"),
    h("span", { class: "scan-line" }),
  );
  const hint = h("p", { class: "scan-hint" }, t("scanHint"));
  show(
    stage,
    h("div", { class: "scan-overlay" }, frame, hint),
    h(
      "div",
      { class: "hud hud-top" },
      h("span", { class: "hud-title", lang: locale, dir: "auto" }, titleText()),
      h(
        "button",
        { type: "button", class: "round", onclick: () => stopAndReturn(), "aria-label": t("stop") },
        icon("close"),
      ),
    ),
    h(
      "div",
      { class: "hud hud-bottom", "aria-live": "polite" },
      status,
      button(t("view3d"), () => switchTo3d(), "btn btn-glass", "cube"),
    ),
  );
  const setStatus = (text: string, found: boolean) => {
    status.replaceChildren(icon(found ? "lock" : "scan", 16), h("span", {}, text));
    status.classList.toggle("is-found", found);
    frame.classList.toggle("is-found", found);
    hint.hidden = found;
  };

  const [loaded, ar] = await Promise.all([ready(), import("./ar.ts")]);
  if (!loaded) return;
  try {
    running = await ar.startAr(stage, loaded, locale, {
      onFound: () => {
        navigator.vibrate?.(25);
        setStatus(t("found"), true);
      },
      onLost: () => setStatus(t("searching"), false),
      onLink: openLink,
    });
    setStatus(t("searching"), false);
  } catch (error) {
    intro(error instanceof ar.ArError ? error.problem : "failed");
  }
}

function switchTo3d() {
  running?.stop();
  running = null;
  void open3d();
}

// ---------- the room (WebXR) ----------

async function startRoom(xrMode: "world" | "surface") {
  const message = h("span", {}, t("starting"));
  const status = h("div", { class: "pill pill-xr" }, icon(xrMode === "world" ? "scan" : "hand", 16), message);
  const again = button(t("xr.again"), () => session?.replace(), "btn btn-glass", "refresh");
  const instead = button(t("xr.tapInstead"), () => session?.tapInstead(), "btn btn-glass");
  again.hidden = true;
  instead.hidden = true;
  const overlay = h(
    "div",
    { class: "xr-overlay" },
    h(
      "div",
      { class: "hud hud-top" },
      status,
      h(
        "button",
        { type: "button", class: "round", onclick: () => session?.stop(), "aria-label": t("xr.exit") },
        icon("close"),
      ),
    ),
    h("div", { class: "hud hud-bottom" }, instead, again),
  );
  // Taps on the overlay's own buttons must not also place the scene.
  for (const b of [again, instead, overlay.querySelector(".round")]) {
    b?.addEventListener("beforexrselect", (event) => event.preventDefault());
  }
  show(
    h(
      "div",
      { class: "screen center", role: "status" },
      h("span", { class: "loader", "aria-hidden": "true" }),
    ),
    overlay,
  );

  let session: Awaited<ReturnType<typeof import("./xr.ts")["startXr"]>> | null = null;
  let current: XrStatus | null = null;
  let insteadTimer = 0;
  const [loaded, xr] = await Promise.all([ready(), import("./xr.ts")]);
  if (!loaded) return;
  const words: Record<XrStatus, MessageKey> = {
    scanning: mode === "wall" ? "xr.scanningWall" : "xr.scanning",
    place: "xr.place",
    placed: "xr.placed",
    aim: "xr.aim",
    locked: "xr.locked",
    manual: "xr.manual",
  };
  try {
    session = await xr.startXr(overlay, loaded, locale, xrMode, {
      onLink: openLink,
      onStatus(next) {
        message.textContent = t(words[next]);
        status.classList.toggle("is-found", next === "locked" || next === "placed");
        again.hidden = next !== "placed" && next !== "locked";
        current = next;
        // After a while without the code, offer tapping the card instead.
        if (next !== "aim") instead.hidden = true;
        else if (!insteadTimer) {
          insteadTimer = window.setTimeout(() => {
            if (current === "aim") instead.hidden = false;
          }, 8000);
        }
      },
      onEnd() {
        clearTimeout(insteadTimer);
        running = null;
        intro();
      },
    });
    running = session;
  } catch (error) {
    console.warn("[viewer] xr", error);
    // No session (refused, or no ARCore): the card keeps working, surfaces fall back to 3D.
    if (xrMode === "world") return startCamera();
    void open3d("noRoom");
  }
}

// ---------- 3D ----------

async function open3d(problem?: "noRoom") {
  const stage = h("div", { class: "stage stage-3d" });
  show(
    stage,
    h(
      "div",
      { class: "hud hud-top" },
      h(
        "button",
        { type: "button", class: "round", onclick: () => stopAndReturn(), "aria-label": t("back") },
        icon("back"),
      ),
      h("span", { class: "hud-title", lang: locale, dir: "auto" }, titleText()),
      h(
        "button",
        { type: "button", class: "round", onclick: () => void share(titleText()), "aria-label": t("share") },
        icon("share"),
      ),
    ),
    h(
      "div",
      { class: "hud hud-bottom" },
      h("span", { class: "pill" }, icon("hand", 16), problem ? t("xr.noRoom") : t("dragHint")),
    ),
  );
  const [loaded, { start3d }] = await Promise.all([ready(), import("./view3d.ts")]);
  if (!loaded) return;
  running = start3d(stage, loaded, locale, { onLink: openLink });
}

function stopAndReturn() {
  running?.stop();
  running = null;
  intro();
}

/** Computers get the 3D view, plus a QR code to continue on a phone. */
async function desktopView() {
  const stage = h("div", { class: "stage stage-3d" });
  const qr = h("div", { class: "qr", "aria-hidden": "true" });
  const phoneMode = mode !== "3d";
  show(
    h(
      "div",
      { class: "desktop" },
      h(
        "main",
        { class: "desktop-stage" },
        stage,
        h("p", { class: "pill hint" }, icon("hand", 16), t("dragHint")),
      ),
      h(
        "aside",
        { class: "desktop-side" },
        h("div", { class: "side-top" }, badge(mode), languageSwitch()),
        title(),
        h("p", { class: "lead" }, t(mode === "3d" ? "intro.3d" : `intro.${mode}`)),
        phoneMode &&
          h(
            "div",
            { class: "qr-card" },
            qr,
            h(
              "div",
              {},
              h("h2", {}, t("desktopTitle")),
              h("p", { class: "muted" }, t("desktopBody"), " ", t(`desktop.${mode as Exclude<Mode, "3d">}`)),
            ),
          ),
        button(t("share"), () => void share(titleText()), "btn btn-quiet", "share"),
        brand(true),
      ),
    ),
  );
  if (phoneMode) {
    const { toString: qrSvg } = await import("qrcode");
    // The page's own address, without any ?lang=, so the phone picks its own language.
    qr.innerHTML = await qrSvg(`${location.origin}${location.pathname}`, {
      type: "svg",
      margin: 1,
      color: { dark: "#0b1320", light: "#ffffff" },
    });
  }
  const [loaded, { start3d }] = await Promise.all([ready(), import("./view3d.ts")]);
  if (!loaded) return;
  running = start3d(stage, loaded, locale, { onLink: openLink, autoRotate: true });
}

// ---------- collections ----------

type CollectionPreview = {
  code: string;
  title: Record<string, string>;
  description?: Record<string, string>;
  defaultLocale: string;
  items: { code: string }[];
};

async function collectionPage(code: string) {
  loading();
  const result = await fetchCollection(code);
  if (result.kind !== "ok") {
    show(
      h(
        "main",
        { class: "screen center" },
        h("div", { class: "empty-art", "aria-hidden": "true" }, icon("scan", 40)),
        h("h1", {}, t("collection.notFound")),
        brand(),
      ),
    );
    return;
  }
  const collection = result.manifest as CollectionPreview;
  const textIn = (text: Record<string, string> | undefined) => {
    if (!text) return "";
    const available = Object.keys(text);
    return text[pickLocale(available, collection.defaultLocale, preferred)] ?? "";
  };
  const name = textIn(collection.title);
  document.title = name;
  const grid = h("ul", { class: "collection-grid" });
  const count = h("span", { class: "badge" }, icon("cube", 14), t("loading"));
  show(
    h(
      "main",
      { class: "collection" },
      h(
        "header",
        { class: "collection-head" },
        h("div", { class: "side-top" }, count, languageSwitch()),
        h("h1", { dir: "auto" }, name),
        collection.description && h("p", { class: "lead", dir: "auto" }, textIn(collection.description)),
      ),
      grid,
      brand(),
    ),
  );
  // Each experience's own manifest gives its current title and cover; unpublished ones drop out.
  const items = await Promise.all(
    collection.items.map(async ({ code: itemCode }) => {
      const item = await fetchManifest(itemCode);
      return item.kind === "ok" ? (item.manifest as ManifestPreview) : null;
    }),
  );
  const live = items.filter((m): m is ManifestPreview => m !== null);
  count.replaceChildren(icon("cube", 14), t("collection.count", { count: String(live.length) }));
  if (live.length === 0) grid.replaceWith(h("p", { class: "muted center-text" }, t("collection.empty")));
  for (const m of live) {
    const cover = coverUrl(m);
    const itemLocale = pickLocale(m.locales ?? [], m.defaultLocale, preferred);
    const itemTitle = m.title?.[itemLocale] ?? Object.values(m.title ?? {})[0] ?? "";
    grid.append(
      h(
        "li",
        {},
        h(
          "a",
          { class: "collection-card", href: `/${m.code}${asked ? `?lang=${asked}` : ""}` },
          h(
            "span",
            { class: `card-art${cover ? "" : " hero-art"}` },
            cover && h("img", { src: cover, alt: "", loading: "lazy", decoding: "async" }),
          ),
          h(
            "span",
            { class: "card-body" },
            badge(modeOf(m.anchor)),
            h("strong", { lang: itemLocale, dir: "auto" }, itemTitle),
            h("span", { class: "card-open" }, t("collection.open"), icon("arrow", 16)),
          ),
        ),
      ),
    );
  }
}

// ---------- links ----------

/**
 * Links leave the experience. Children's experiences always explain that first and suggest
 * asking a grown-up (docs/ARCHITECTURE.md §7.3); others open in a new tab.
 */
function openLink(url: string) {
  let target: URL;
  try {
    target = new URL(url);
  } catch {
    return;
  }
  if (target.protocol !== "https:") return;
  const go = () => window.open(target.href, "_blank", "noopener,noreferrer");
  if (!manifest.audience?.children) {
    go();
    return;
  }
  const dialog = h(
    "dialog",
    { class: "leaving", "aria-labelledby": "leaving-title" },
    h("h2", { id: "leaving-title" }, t("leaving.title")),
    h("p", {}, t("leaving.body", { host: target.hostname })),
    h("p", { class: "strong" }, t("leaving.children")),
    h(
      "div",
      { class: "actions" },
      button(t("leaving.stay"), () => dialog.close(), "btn btn-quiet"),
      button(t("leaving.go"), () => {
        dialog.close();
        go();
      }),
    ),
  );
  dialog.addEventListener("close", () => dialog.remove());
  // In a WebXR session the page isn't visible; the overlay is.
  (document.querySelector(".xr-overlay") ?? document.body).append(dialog);
  dialog.showModal();
}

// Release the camera when the page goes away (back button, closed tab).
addEventListener("pagehide", () => running?.stop());

void boot();
